"use client";

import { useAuthActions } from "@convex-dev/auth/react";
import { useAction } from "convex/react";
import { ArrowLeft, ArrowLeftRight, ArrowRight, Loader2, Mail } from "lucide-react";
import { useEffect, useState, type MutableRefObject, type ReactNode } from "react";
import { api } from "../../../convex/_generated/api";
import { friendlyConvexError } from "@/studio/lib/convexUserErrors";
import { AuthPasswordField } from "./StudioAuthGate";
import { signupPreviewHost } from "./signupWizard";

/**
 * Email-first sign-in. identify -> password, or -> code (backup or Studio email)
 * -> add backup (if none) -> set password -> session. The server refuses a
 * session until the backup is verified and a password exists.
 */
export type SignInStage = "identify" | "password" | "code" | "backup-add" | "set-password";

const STAGES: SignInStage[] = ["identify", "password", "code", "backup-add", "set-password"];

/** Preview/localhost screenshots: `?signinStep=code` (add `&signinVia=code` for code sign-in). Never calls the server. */
export function signinScreenshotStage(hostname: string, search: string): SignInStage | null {
  if (!signupPreviewHost(hostname)) return null;
  const raw = new URLSearchParams(search.replace(/^\?/, "")).get("signinStep");
  return STAGES.includes(raw as SignInStage) ? (raw as SignInStage) : null;
}

const PREVIEW_ID = "ada@yatishara.com";
const PREVIEW_SENT = "ada.backup@example.com";

type Advance = { next: string; loginEmail?: string; ticket?: string; sentTo?: string; magicToken?: string };

/** Why the code step is showing: a reset, an emailed sign-in code, or account setup. */
type CodeMode = "reset" | "signin" | null;

export function EmailFirstSignIn({
  submitRef,
  afterPrimary,
}: {
  submitRef: MutableRefObject<(() => void) | null>;
  domain: string;
  /** Rendered after "Email me a sign-in code" and before "Forgot password?" (e.g. Continue with Google). */
  afterPrimary?: ReactNode;
}) {
  const { signIn } = useAuthActions();
  const start = useAction(api.signInFlow.start);
  const submitPassword = useAction(api.signInFlow.submitPassword);
  const requestReset = useAction(api.signInFlow.requestReset);
  const requestSignInCode = useAction(api.signInFlow.requestSignInCode);
  const verifyCode = useAction(api.signInFlow.verifyCode);
  const resendCode = useAction(api.signInFlow.resendCode);
  const addBackup = useAction(api.signInFlow.addBackup);
  const setPassword = useAction(api.signInFlow.setPassword);

  const [preview, setPreview] = useState<SignInStage | null>(null);
  const [stage, setStage] = useState<SignInStage>("identify");
  const [identifier, setIdentifier] = useState("");
  const [display, setDisplay] = useState("");
  const [password, setPasswordInput] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [ticket, setTicket] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [mode, setMode] = useState<CodeMode>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const p = signinScreenshotStage(window.location.hostname, window.location.search);
    if (!p) return;
    setPreview(p);
    setStage(p);
    setIdentifier("ada");
    setDisplay(PREVIEW_ID);
    const via = new URLSearchParams(window.location.search.replace(/^\?/, "")).get("signinVia");
    if (p === "code" && via === "code") {
      setMode("signin");
      setSentTo("");
    } else {
      setSentTo(p === "code" ? PREVIEW_SENT : "");
    }
  }, []);

  function backToStart() {
    setStage("identify");
    setPasswordInput("");
    setConfirm("");
    setCode("");
    setTicket("");
    setSentTo("");
    setMode(null);
    setError("");
    setNotice("");
  }

  async function finish(email: string, pw: string) {
    await signIn("password", { flow: "signIn", email, password: pw });
  }

  async function follow(r: Advance) {
    if (r.ticket) setTicket(r.ticket);
    setCode("");
    if (r.next === "signin" && r.magicToken) {
      await signIn("magic-link", { token: r.magicToken });
      return;
    }
    if (r.next === "signin" && r.loginEmail) {
      await finish(r.loginEmail, password);
      return;
    }
    if (r.next === "code") {
      setSentTo(r.sentTo ?? "");
      setNotice("");
    }
    if (r.next === "code" || r.next === "backup-add" || r.next === "set-password" || r.next === "password") {
      setStage(r.next);
    }
  }

  function run(task: () => Promise<void>) {
    if (pending) return;
    setPending(true);
    setError("");
    void task()
      .catch((err: unknown) => setError(friendlyConvexError(err, "Something went wrong")))
      .finally(() => setPending(false));
  }

  function submit() {
    if (preview) return;
    if (stage === "identify") {
      const typed = identifier.trim();
      if (!typed) return setError("Enter your name or email");
      run(async () => {
        const r = await start({ identifier: typed });
        setDisplay(r.display);
        setTicket(r.ticket ?? "");
        setSentTo("");
        setStage(r.step);
      });
      return;
    }
    if (stage === "password") {
      if (!password) return setError("Enter your password");
      run(async () => follow(await submitPassword({ identifier, password })));
      return;
    }
    if (stage === "code") {
      if (code.replace(/\D/g, "").length !== 6) return setError("Enter the 6-digit code");
      run(async () => follow(await verifyCode({ ticket, code })));
      return;
    }
    if (stage === "backup-add") {
      run(async () => follow(await addBackup({ ticket, backupEmail: backup })));
      return;
    }
    if (stage === "set-password") {
      if (password.length < 8) return setError("Password must be at least 8 characters");
      if (password !== confirm) return setError("Passwords don't match");
      run(async () => {
        const r = await setPassword({ ticket, password });
        setLoginEmail(r.loginEmail);
        await finish(r.loginEmail, password);
      });
    }
  }
  submitRef.current = submit;

  const copy: Record<SignInStage, string> = {
    identify: "",
    password: "",
    code: sentTo
      ? `Enter the 6-digit code we sent to ${sentTo}.`
      : "If this account exists, we sent a 6-digit code to its email.",
    "backup-add": "Add a backup email. We will send it a code to confirm it.",
    "set-password": "Choose a password for your account.",
  };
  const primary: Record<SignInStage, string> = {
    identify: "Continue",
    password: "Sign in",
    code: mode === "signin" ? "Sign in" : "Verify",
    "backup-add": "Send code",
    "set-password": "Save and sign in",
  };

  return (
    <>
      {stage !== "identify" ? (
        <div className="studio-auth-account-pill" data-signin-account>
          <span className="studio-auth-account-pill-email">{display || loginEmail}</span>
          <button
            type="button"
            className="studio-auth-account-switch"
            onClick={backToStart}
            disabled={pending}
            aria-label="Use a different account"
            title="Use a different account"
          >
            <ArrowLeftRight aria-hidden="true" />
          </button>
        </div>
      ) : null}
      {copy[stage] ? (
        <p className="studio-auth-copy">{mode === "reset" && stage === "code" ? `${copy.code} Then set a new password.` : copy[stage]}</p>
      ) : null}

      {stage === "identify" ? (
        <label className="block">
          <span className="studio-auth-field is-email">
            <Mail className="studio-auth-accent-text" aria-hidden="true" />
            <input
              name="email"
              placeholder="Name or email"
              aria-label="Name or email"
              type="text"
              autoComplete="username"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoFocus
            />
          </span>
        </label>
      ) : null}

      {stage === "password" ? (
        <>
          <input type="hidden" name="email" autoComplete="username" value={display} readOnly />
          <AuthPasswordField
            password={password}
            onPassword={setPasswordInput}
            autoComplete="current-password"
            autoFocus
            lengthHint={false}
          />
        </>
      ) : null}

      {stage === "code" ? (
        <>
          <input
            className="studio-auth-field is-code is-email-otp"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
              setError("");
            }}
            placeholder="000-000"
            aria-label="6-digit code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
          />
          <button
            type="button"
            className="studio-auth-link w-full py-1 text-center"
            disabled={pending}
            onClick={() => {
              if (preview) return;
              run(async () => {
                await resendCode({ ticket });
                setNotice("A new code is on its way.");
              });
            }}
          >
            Resend code
          </button>
        </>
      ) : null}

      {stage === "backup-add" ? (
        <label className="block">
          <span className="studio-auth-field is-email">
            <Mail className="studio-auth-accent-text" aria-hidden="true" />
            <input
              value={backup}
              onChange={(e) => setBackup(e.target.value)}
              placeholder="Backup email"
              aria-label="Backup email"
              type="email"
              autoComplete="email"
              autoFocus
            />
          </span>
        </label>
      ) : null}

      {stage === "set-password" ? (
        <>
          <input type="hidden" name="email" autoComplete="username" value={loginEmail || display} readOnly />
          <AuthPasswordField password={password} onPassword={setPasswordInput} autoComplete="new-password" autoFocus />
          <AuthPasswordField
            placeholder="Confirm password"
            password={confirm}
            onPassword={setConfirm}
            autoComplete="new-password"
            lengthHint={false}
          />
        </>
      ) : null}

      {notice ? <p className="studio-auth-notice m-0 text-center">{notice}</p> : null}
      {error ? <p className="studio-auth-error-box">{error}</p> : null}
      <button className="studio-auth-primary" type="submit" disabled={pending}>
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <>
            {primary[stage]}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </>
        )}
      </button>
      {stage === "password" ? (
        <button
          type="button"
          className="studio-auth-secondary"
          disabled={pending}
          onClick={() => {
            if (preview) return;
            run(async () => {
              const r = await requestSignInCode({ identifier });
              setTicket(r.ticket);
              setSentTo("");
              setMode("signin");
              setCode("");
              setPasswordInput("");
              setStage("code");
            });
          }}
        >
          <Mail className="h-4 w-4" aria-hidden="true" />
          Email me a sign-in code
        </button>
      ) : null}
      {stage === "identify" || stage === "password" ? afterPrimary : null}
      {/* Last item in the sign-in card, under "Email me a sign-in code". */}
      {stage === "password" ? (
        <button
          type="button"
          className="studio-auth-link w-full py-1 text-center"
          disabled={pending}
          onClick={() => {
            if (preview) return;
            run(async () => {
              const r = await requestReset({ identifier });
              setTicket(r.ticket);
              setSentTo("");
              setMode("reset");
              setCode("");
              setStage("code");
            });
          }}
        >
          Forgot password?
        </button>
      ) : null}
    </>
  );
}
